import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import _ from 'lodash';
import { InstalledBaseNode, InstalledBaseNodeType } from '../codegen/graphql';
import { InstalledBaseNodeTypeList } from '../graphql/queries/installedBaseNodeTypeList';
import { useIndustryType } from './useIndustryType';
import { childRuleHasRoom, getChildRule, isChildTypeWhitelisted } from '../utils/nodeChildRules';

/**
 * The node types that may be created under `node`, as agreed by the type catalogue
 * (`InstalledBaseNodeTypeList`, which carries the `recommended` flag for this parent type)
 * and the parent's structural rules (`nodeRules.childRules`, IBM-462).
 *
 * Shared by the create-node type selection and the empty-column guidance so the two can
 * never disagree about what is creatable. Apollo caches per
 * (industryType, parentInstalledBaseNodeTypeKey), so sibling columns of the same parent
 * type resolve from cache.
 *
 * Note the deliberate split: the selection screen lists every *whitelisted* type and
 * greys out the ones at their count limit, while the guidance may only offer types that
 * can actually be created right now (`creatable`).
 */
export interface AllowedChildNodeTypes {
    loading: boolean;
    error: boolean;
    /** Structurally allowed types, ASSET first — including ones at their count limit. */
    whitelisted: InstalledBaseNodeType[];
    /** Whitelisted and recommended for this parent type. */
    recommended: InstalledBaseNodeType[];
    /** Whitelisted but not recommended. */
    other: InstalledBaseNodeType[];
    /** Whitelisted types that still have room, i.e. creatable right now. */
    creatable: InstalledBaseNodeType[];
}

export const useAllowedChildNodeTypes = (node: InstalledBaseNode): AllowedChildNodeTypes => {
    const fallbackIndustryType = useIndustryType();
    const industryType = node?.owner?.industryType ?? fallbackIndustryType;
    const parentTypeKey = node?.nodeType?.key;

    const { data, loading, error } = useQuery(InstalledBaseNodeTypeList, {
        variables: {
            industryType,
            parentInstalledBaseNodeTypeKey: parentTypeKey,
        },
        skip: !parentTypeKey,
    });

    const nodeRules = node?.nodeRules;

    return useMemo(() => {
        const nodeTypes: InstalledBaseNodeType[] = _.get(
            data,
            'installedBaseNodeTypeList.installedBaseNodeTypes',
            []
        );

        // Types that can never be created under this parent are dropped entirely.
        const whitelisted = _.orderBy(
            nodeTypes.filter((nt) => isChildTypeWhitelisted(nodeRules, nt.key)),
            (nt: InstalledBaseNodeType) => (nt.key === 'ASSET' ? 0 : 1)
        );

        return {
            // Only a loading state while there is nothing to show yet; a cache hit or a
            // background refetch must not blank out the consumer.
            loading: loading && !nodeTypes.length,
            error: !!error,
            whitelisted,
            recommended: whitelisted.filter((nt) => nt.recommended),
            other: whitelisted.filter((nt) => !nt.recommended),
            creatable: whitelisted.filter((nt) => {
                const rule = getChildRule(nodeRules, nt.key);
                return !rule || childRuleHasRoom(rule);
            }),
        };
    }, [data, loading, error, nodeRules]);
};
